import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { GamePicks } from '../components/GamePicks';
import { LeaderboardList } from '../components/LeaderboardList';
import { PlayerCard } from '../components/PlayerCard';
import { PlayerSheet } from '../components/PlayerSheet';
import { RosterPanel } from '../components/RosterPanel';
import { WagerPrompt, WagerSettlement } from '../components/Wager';
import { setLatePick, setWagerIn as saveWagerIn, setWagerPaid } from '../lib/db';
import { phasesByGame } from '../lib/engine/phase';
import { SalaryBar } from '../components/SalaryBar';
import { ScoreOverlay } from '../components/ScoreOverlay';
import { Banner, Empty, KeyValue, Spinner, SportPill, StatusPill, Toast } from '../components/ui';
import { useContestData } from '../hooks/useContestData';
import { useScoringEvents } from '../hooks/useScoringEvents';
import { usePointDeltas } from '../hooks/usePointDeltas';
import { ScoringTab } from '../components/ScoringTab';
import { ScoreStrip } from '../components/ScoreStrip';
import { useLiveSync } from '../hooks/useLiveSync';
import { usePendingSpreads } from '../hooks/usePendingSpreads';
import { useCopyableLineups, type CopyableLineup } from '../hooks/useCopyableLineups';
import { joinContest, saveEntry } from '../lib/db';
import { formatCountdown, formatDateTime, formatGameTime } from '../lib/engine/contestState';
import { buildLeaderboard } from '../lib/engine/leaderboard';
import { formatMoney, validateEntry } from '../lib/engine/lineup';
import { hasSpread, pickOpen } from '../lib/engine/spread';
import { isEligible, rosterSummary } from '../lib/engine/roster';
import { markEntered, teamNameFor } from '../lib/identity';
import { generateTeamName } from '../lib/teamName';
import { captainEnabled, captainMultiplier, captainPremium } from '../lib/engine/captain';
import { statMeta } from '../lib/stats';
import { useSession } from '../state/SessionContext';
import { SPORT_LABELS, type ContestPlayer, type LineupSelection, type Sport } from '../types';

type Tab = 'lineup' | 'board' | 'scoring' | 'info';
type SortKey = 'salary' | 'projection' | 'points';

/** Entering runs in this order; a contest without picks or money skips those. */
type EntryStep = 'roster' | 'picks' | 'wager';

export function ContestPage() {
  const { contestId } = useParams<{ contestId: string }>();
  const { uid, identity } = useSession();
  const data = useContestData(contestId, uid);
  const { contest, players, playersById, standings, entries, myEntry, locked, status } = data;

  const [tab, setTab] = useState<Tab>('lineup');
  const [lineup, setLineup] = useState<LineupSelection[]>([]);
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [activeSlotId, setActiveSlotId] = useState<string | null>(null);
  const [sportFilter, setSportFilter] = useState<Sport | 'all'>('all');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('salary');
  const [detail, setDetail] = useState<ContestPlayer | null>(null);
  const [message, setMessage] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);
  /*
   * The answer to a tap, as opposed to the state of the page.
   *
   * Refusing a tap used to be written into the banner at the top of the page,
   * which someone building a lineup has long since scrolled past — so the
   * button they pressed simply looked dead. This floats over whatever they are
   * looking at and clears itself.
   */
  const [toast, setToast] = useState<{ tone: 'ok' | 'bad' | 'warn'; text: string } | null>(null);
  /** Whether this person is in on the money. Null until they have said. */
  const [wagerIn, setWagerIn] = useState<boolean | null>(null);
  /** Picks made since the contest locked, shown before Firestore echoes them. */
  const [latePicks, setLatePicks] = useState<Record<string, string>>({});
  // A pick closes when its own game starts, which is a moment that arrives
  // while the page is open; this re-reads the clock so it closes on time.
  const [clock, setClock] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => setClock((value) => value + 1), 20_000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 4500);
    return () => window.clearTimeout(timer);
  }, [toast]);
  const [saving, setSaving] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [teamName, setTeamName] = useState<string>('');
  const picksRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useSearchParams();

  /*
   * Adopt the saved entry once, then let local edits stand.
   *
   * It only counts as "once" if it happens before anyone starts picking. On a
   * slow connection the saved entry can land a minute after the page does, and
   * adopting it then wipes whatever has been chosen in the meantime — which
   * looks exactly like a roster that will not fill up.
   */
  useEffect(() => {
    if (hydrated || !myEntry) return;
    if (lineup.length > 0 || Object.keys(picks).length > 0) {
      setHydrated(true);
      return;
    }
    setLineup(myEntry.lineup ?? []);
    setPicks(myEntry.picks ?? {});
    if (myEntry.teamName) setTeamName(myEntry.teamName);
    // Already entered, so open on the standings rather than the builder —
    // unless a line has posted since, in which case the one thing they came
    // back for is the game they could not call yet.
    const unpicked = contest?.gameWinner.enabled
      ? contest.games.filter((game) => hasSpread(game) && !(myEntry.picks ?? {})[game.id])
      : [];
    if (unpicked.length > 0 && !locked) {
      setTab('lineup');
      setEntryStep('picks');
    } else {
      setTab('board');
    }
    setHydrated(true);
  }, [myEntry, hydrated, lineup.length, picks]);

  useEffect(() => {
    if (locked) setTab((current) => (current === 'lineup' ? 'board' : current));
    if (locked) setSortKey('points');
  }, [locked]);


  const myPlayerIds = useMemo(
    () => new Set((myEntry?.lineup ?? lineup).map((line) => line.playerId)),
    [myEntry, lineup],
  );
  const { events } = useScoringEvents(contest, players, myPlayerIds, locked && status !== 'complete');
  // Anyone watching a live contest keeps its scores moving.
  const liveSync = useLiveSync(contest, status === 'live');
  // Runs while anything is still to be called, which outlasts the lock.
  usePendingSpreads(contest, status !== 'complete');
  const copyable = useCopyableLineups(contest, uid, !locked && tab === 'lineup');

  /**
   * A shared link carries the join code, so following one puts you in the
   * contest without typing anything. The code is dropped from the address bar
   * afterwards so a refresh does not try again.
   */
  useEffect(() => {
    const invite = query.get('j');
    if (!invite || !contest || invite !== contest.joinCode) return;
    if (!contest.members.includes(uid)) void joinContest(contest.id, uid);
    const next = new URLSearchParams(query);
    next.delete('j');
    setQuery(next, { replace: true });
  }, [query, contest, uid, setQuery]);
  // Who just scored, shown everywhere a player appears.
  const pointDeltas = usePointDeltas(players, locked);

  const cap = contest?.salaryCapInfo.cap ?? 0;
  const hasCaptain = captainEnabled(contest);
  const capMultiplier = captainMultiplier(contest);
  const captainPlayerId = lineup.find((line) => line.captain)?.playerId ?? null;
  const validation = useMemo(() => {
    if (!contest) return null;
    return validateEntry(contest, lineup, picks, playersById);
  }, [contest, lineup, picks, playersById]);

  // Once the entry is complete, name the team.
  useEffect(() => {
    if (locked || teamName || !identity || !validation?.valid) return;
    setTeamName(generateTeamName(identity.firstName));
  }, [locked, teamName, identity, validation?.valid]);

  const leaderboard = useMemo(() => {
    if (!contest) return [];
    return buildLeaderboard({
      contest,
      standings,
      entries: locked ? entries : myEntry ? [myEntry] : [],
      players: playersById,
      selfUid: uid,
      locked,
    });
  }, [contest, standings, entries, myEntry, playersById, uid, locked]);

  // Which games have started and which have ended, so every number on the page
  // can say whether it is a projection, a score so far, or a final one.
  const phases = useMemo(() => (contest ? phasesByGame(contest) : new Map()), [contest]);

  /*
   * Entering is three things in order: pick a team, call the games, say whether
   * money is riding on it. Each was on one long page, which meant scrolling
   * past a half-finished roster to find out the submit button was refusing for
   * a reason written somewhere else. One at a time, and each only lets you on
   * once it is done.
   */
  const steps = useMemo(
    () =>
      ['roster', ...(contest?.gameWinner.enabled ? ['picks' as const] : []), ...(contest?.wager ? ['wager' as const] : [])] as EntryStep[],
    [contest?.gameWinner.enabled, contest?.wager],
  );
  const [entryStep, setEntryStep] = useState<EntryStep>('roster');
  useEffect(() => {
    if (!steps.includes(entryStep)) setEntryStep('roster');
  }, [steps, entryStep]);

  const myStanding = useMemo(() => standings.find((row) => row.uid === uid), [standings, uid]);
  // Their recorded answer wins over anything typed in this tab.
  useEffect(() => {
    if (myStanding?.wagerIn !== undefined) setWagerIn(myStanding.wagerIn);
  }, [myStanding?.wagerIn]);

  const rosterComplete = Boolean(validation && validation.filledSlots === contest?.rosterSlots.length);
  // A game with no posted line cannot be picked, so it cannot hold up a lineup.
  const pickableGames = useMemo(
    () => (contest?.gameWinner.enabled ? contest.games.filter(hasSpread) : []),
    [contest],
  );
  const openPicks = pickableGames.filter((game) => !picks[game.id]).length;

  /*
   * Picks outlive the contest's lock.
   *
   * The roster freezes when the first game starts, because every player in it
   * is about to be in play. A pick on a game that starts tomorrow is not, so it
   * stays open until that game does.
   */
  const effectivePicks = useMemo(
    () => ({ ...(myEntry?.picks ?? {}), ...(myStanding?.picks ?? {}), ...latePicks }),
    [myEntry?.picks, myStanding?.picks, latePicks],
  );
  /** Games still pickable: not started, whatever the contest's own lock says. */
  const stillOpen = useMemo(
    () => (contest?.gameWinner.enabled ? contest.games.filter((game) => pickOpen(game)) : []),
    [contest, clock],
  );
  const unpicked = stillOpen.filter((game) => !effectivePicks[game.id]);
  const unpickedWithLine = unpicked.filter(hasSpread);
  const hasEntry = Boolean(myEntry);
  /** Late picking only matters once the entry itself is frozen. */
  const latePicksOpen = locked && hasEntry && stillOpen.length > 0;

  /*
   * How many are in on the money. Counted from the public standings, with this
   * person's own answer taken from whatever they have just tapped — the written
   * record can be a moment behind, and the pot should not lag their own choice.
   */
  const bettors = useMemo(() => {
    const others = standings.filter((row) => row.uid !== uid && row.wagerIn === true).length;
    const mine = wagerIn === null ? myStanding?.wagerIn === true : wagerIn;
    return others + (mine ? 1 : 0);
  }, [standings, uid, wagerIn, myStanding?.wagerIn]);
  /** Roster rules only — the picks and the wager are later steps. */
  const isPickError = (error: string) => /^Pick a winner|^Invalid winner/.test(error);
  const rosterReady = Boolean(
    validation && rosterComplete && !validation.errors.some((error) => !isPickError(error)),
  );
  /** Games whose line has posted but which have not been called yet. */
  const openPickGames = useMemo(
    () => pickableGames.filter((game) => !picks[game.id]),
    [pickableGames, picks],
  );
  /** Games still waiting on a line; they can be called later, before lock. */
  const awaitingLine = useMemo(
    () => (contest?.gameWinner.enabled ? contest.games.filter((game) => !hasSpread(game)) : []),
    [contest],
  );
  const picksReady = openPickGames.length === 0;
  const wagerReady = !contest?.wager || wagerIn !== null;
  const stepReady: Record<EntryStep, boolean> = {
    roster: rosterReady,
    picks: picksReady,
    wager: wagerReady,
  };
  const stepIndex = Math.max(0, steps.indexOf(entryStep));
  const canSubmit = Boolean(validation?.valid) && picksReady && wagerReady;
  /** The one thing stopping this step, phrased for the step you are on. */
  const stepBlocker = (() => {
    if (entryStep === 'roster') {
      const error = validation?.errors.find((e) => !isPickError(e));
      return rosterReady ? null : error ?? null;
    }
    if (entryStep === 'picks') {
      return picksReady
        ? null
        : `${openPickGames.length} game${openPickGames.length === 1 ? '' : 's'} still to call`;
    }
    return wagerReady ? null : 'Say whether you are in on the money';
  })();

  // With the roster done but picks outstanding, the button moves the user on to
  // the picks rather than sitting disabled.

  const activeSlot = contest?.rosterSlots.find((slot) => slot.id === activeSlotId) ?? null;
  const occupantOfActiveSlot = activeSlotId ? lineup.find((line) => line.slotId === activeSlotId) : undefined;
  const salaryUsed = validation?.salaryUsed ?? 0;
  const spendable = cap - salaryUsed + (occupantOfActiveSlot ? playersById.get(occupantOfActiveSlot.playerId)?.salary ?? 0 : 0);

  const rosterablePlayers = useMemo(
    () => players.filter((player) => contest?.rosterSlots.some((slot) => isEligible(player, slot))),
    [players, contest],
  );

  const visiblePlayers = useMemo(() => {
    let list = rosterablePlayers;
    if (activeSlot) list = list.filter((player) => isEligible(player, activeSlot));
    if (sportFilter !== 'all') list = list.filter((player) => player.sport === sportFilter);
    const term = search.trim().toLowerCase();
    if (term) {
      list = list.filter(
        (player) =>
          player.name.toLowerCase().includes(term) ||
          player.teamAbbr.toLowerCase().includes(term) ||
          player.position.toLowerCase().includes(term),
      );
    }
    const sorted = [...list];
    sorted.sort((a, b) => {
      if (sortKey === 'projection') return b.projection.normalized - a.projection.normalized;
      if (sortKey === 'points') return (b.normalizedPoints ?? 0) - (a.normalizedPoints ?? 0);
      return b.salary - a.salary;
    });
    return sorted;
  }, [rosterablePlayers, activeSlot, sportFilter, search, sortKey]);

  if (!contest) {
    return (
      <main className="page">
        {data.loading ? <Spinner label="Loading contest…" /> : <Empty title="Contest not found" />}
      </main>
    );
  }

  const usedPlayerIds = new Set(lineup.map((line) => line.playerId));

  /**
   * Tap to roster a player, tap again to drop them. Nothing else, so dropping
   * someone can never fail and can never disturb the captain: the captain is
   * chosen on the roster itself.
   */
  function assignPlayer(player: ContestPlayer) {
    if (!contest || locked) return;
    setMessage(null);

    if (lineup.some((line) => line.playerId === player.id)) {
      setLineup((current) => current.filter((line) => line.playerId !== player.id));
      return;
    }

    const target =
      activeSlot && isEligible(player, activeSlot)
        ? activeSlot
        : contest.rosterSlots.find(
            (slot) => isEligible(player, slot) && !lineup.some((line) => line.slotId === slot.id),
          );
    if (!target) {
      setToast({ tone: 'bad', text: `No open roster spot for ${player.position}. Tap a spot to replace it.` });
      return;
    }

    // Whoever holds the target spot is refunded before the cap is checked.
    const replaced = lineup.find((line) => line.slotId === target.id);
    const replacedPlayer = replaced ? playersById.get(replaced.playerId) : undefined;
    const refund = replacedPlayer
      ? replacedPlayer.salary + (replaced?.captain ? captainPremium(replacedPlayer, capMultiplier) : 0)
      : 0;
    const available = cap - salaryUsed + refund;
    if (player.salary > available) {
      setToast({
        tone: 'bad',
        text: `${player.name} costs ${formatMoney(player.salary)} and you have ${formatMoney(
          available,
        )} left. Drop someone first.`,
      });
      return;
    }

    setLineup((current) => [
      ...current.filter((line) => line.slotId !== target.id && line.playerId !== player.id),
      { slotId: target.id, playerId: player.id },
    ]);
    setActiveSlotId(null);
  }

  /** Promoting costs the difference between the two captains' premiums. */
  function promoteToCaptain(slotId: string, player: ContestPlayer) {
    if (!contest || locked || !hasCaptain) return;
    const currentCaptain = lineup.find((line) => line.captain);
    const currentPlayer = currentCaptain ? playersById.get(currentCaptain.playerId) : undefined;
    const released = currentPlayer ? captainPremium(currentPlayer, capMultiplier) : 0;
    const cost = captainPremium(player, capMultiplier) - released;

    /*
     * The captaincy moves even when it costs more than is left.
     *
     * Refusing was correct arithmetic and the wrong answer: a roster built to
     * the cap has nothing spare, so naming a captain — the last thing anyone
     * does — did nothing at all, and the explanation was something they had to
     * notice rather than something they could act on. Letting it through makes
     * the tap work and turns the problem into one the salary bar already states
     * plainly and the submit button already refuses to ignore: a lineup over
     * the cap, with the amount to find and somebody to drop named here.
     */
    const over = cost - (cap - salaryUsed);
    if (over > 0) {
      const candidate = lineup
        .filter((line) => line.slotId !== slotId)
        .map((line) => playersById.get(line.playerId))
        .filter((entry): entry is ContestPlayer => Boolean(entry) && entry!.salary >= over)
        .sort((a, b) => a.salary - b.salary)[0];
      setToast({
        tone: 'warn',
        text: `${player.name} is captain — that puts you ${formatMoney(over)} over the cap.${
          candidate ? ` Drop ${candidate.name} (${formatMoney(candidate.salary)}) to fix it.` : ''
        }`,
      });
    } else {
      setToast(null);
    }
    setLineup((current) => current.map((line) => ({ ...line, captain: line.slotId === slotId })));
  }

  function toggleCaptainSlot(slotId: string) {
    const line = lineup.find((entry) => entry.slotId === slotId);
    const player = line ? playersById.get(line.playerId) : undefined;
    // A tap that cannot be honoured still has to answer. Silence here is what
    // made the button look broken in the first place.
    if (!line || !player) {
      setToast({ tone: 'bad', text: 'That player is still loading. Try again in a moment.' });
      return;
    }
    if (locked) {
      setToast({ tone: 'bad', text: 'The contest has locked, so the roster cannot change.' });
      return;
    }
    if (line.captain) {
      setLineup((current) => current.map((entry) => ({ ...entry, captain: false })));
      return;
    }
    promoteToCaptain(slotId, player);
  }

  /** Captain a player from wherever they are listed, not just the roster. */
  function toggleCaptainForPlayer(playerId: string) {
    const line = lineup.find((entry) => entry.playerId === playerId);
    if (line) toggleCaptainSlot(line.slotId);
  }

  function removeSlot(slotId: string) {
    setLineup((current) => current.filter((line) => line.slotId !== slotId));
  }

  async function submit() {
    if (!contest || !uid || !identity || !validation) return;
    if (!validation.valid) {
      setMessage({ tone: 'bad', text: validation.errors[0] });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      await saveEntry({
        contestId: contest.id,
        uid,
        displayName: identity.displayName,
        lineup,
        picks,
        salaryUsed: validation.salaryUsed,
        ...(contest.wager ? { wagerIn: wagerIn === true } : {}),
        teamName: teamName || undefined,
        lockedSnapshot: Object.fromEntries(
          lineup.map((line) => [
            line.playerId,
            { salary: playersById.get(line.playerId)?.salary ?? 0, slotId: line.slotId },
          ]),
        ),
      });
      markEntered(contest.id);
      setMessage({ tone: 'ok', text: myEntry ? 'Lineup updated.' : 'Lineup submitted. Good luck!' });
      // Saving finishes the job, so show where the entry now stands.
      setTab('board');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setMessage({
        tone: 'bad',
        text:
          error instanceof Error && /permission/i.test(error.message)
            ? 'This contest is locked — lineups can no longer be changed.'
            : 'Could not save your lineup. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  }

  /**
   * Take a lineup built in another contest with the same rules. It is copied
   * into this draft, not linked to it: the two entries are separate documents
   * from here on, so editing one leaves the other alone.
   */
  function copyLineupFrom(source: CopyableLineup) {
    if (
      lineup.length > 0 &&
      !window.confirm(`Replace your lineup here with the one from "${source.contest.name}"?`)
    ) {
      return;
    }
    const inPool = source.lineup.filter((line) => playersById.has(line.playerId));
    setLineup(inPool);
    // Only picks for games that still have a line are worth carrying over.
    setPicks(
      Object.fromEntries(
        Object.entries(source.picks).filter(([gameId]) =>
          contest?.games.some((game) => game.id === gameId && hasSpread(game)),
        ),
      ),
    );
    setMessage({
      tone: 'ok',
      text:
        inPool.length === source.lineup.length
          ? `Copied from ${source.contest.name}. Change it however you like — it is your own entry now.`
          : `Copied ${inPool.length} of ${source.lineup.length} players; the rest are not in this pool.`,
    });
  }

  // One place to open a player's scoring breakdown, from any list that names them.
  const openPlayer = (playerId: string) => {
    const player = players.find((candidate) => candidate.id === playerId);
    if (player) setDetail(player);
  };

  const untilLock = Date.parse(contest.lockTime) - Date.now();
  const myRow = leaderboard.find((row) => row.isSelf);

  return (
    <main className={`page page--wide${tab === 'lineup' && !locked ? ' page--bar' : ''}`}>
      <ScoreOverlay events={events} teamName={teamNameFor(identity)} />

      <div className="stack">
        <div>
          <Link to="/" className="tiny faint">
            ← All contests
          </Link>
          <div className="row row--between" style={{ alignItems: 'flex-start', marginTop: 6, gap: 10 }}>
            <h1 style={{ fontSize: 21, fontWeight: 900 }}>{contest.name}</h1>
            <StatusPill status={status} />
          </div>
          <div className="row row--wrap" style={{ gap: 6, marginTop: 8 }}>
            {contest.sports.map((sport) => (
              <SportPill key={sport} sport={sport} />
            ))}
            <span className="pill">{rosterSummary(contest.rosterSlots)}</span>
            <span className="pill">{formatMoney(cap)} cap</span>
            <span className="pill">{standings.length} entries</span>
          </div>
          <div className="tiny faint" style={{ marginTop: 6 }}>
            {status === 'open'
              ? `Everything locks when the first game starts — ${formatDateTime(contest.lockTime)} (${formatCountdown(untilLock)})`
              : status === 'live'
                ? 'Locked. Live scoring in progress.'
                : 'Contest complete.'}
          </div>
        </div>

        <ScoreStrip
          games={contest.games}
          lastSyncAt={contest.lastSyncAt ?? undefined}
          onRefresh={status === 'live' ? liveSync.syncNow : undefined}
          refreshing={liveSync.syncing}
        />

        <div className="tabs">
          {(['lineup', 'board', 'scoring', 'info'] as Tab[]).map((key) => (
            <button
              key={key}
              type="button"
              className={`tab${tab === key ? ' tab--active' : ''}`}
              onClick={() => setTab(key)}
            >
              {key === 'lineup' && !locked && openPicks > 0 ? <span className="tab__dot" /> : null}
              {key === 'lineup'
                ? locked
                  ? 'My lineup'
                  : 'Build lineup'
                : key === 'board'
                  ? 'Leaderboard'
                  : key === 'scoring'
                    ? 'Scoring'
                    : 'Info'}
            </button>
          ))}
        </div>

        {message ? <Banner tone={message.tone === 'ok' ? 'ok' : 'bad'}>{message.text}</Banner> : null}
        {toast ? <Toast tone={toast.tone}>{toast.text}</Toast> : null}

        {/*
          * Every game still to be called, whether or not its line has posted —
          * a game nobody can pick yet is still a game they have not picked, and
          * the contest page is where they will notice.
          */}
        {hasEntry && unpicked.length > 0 ? (
          <div className="banner banner--warn row row--between" style={{ gap: 10 }}>
            <span style={{ minWidth: 0 }}>
              <strong>
                {unpicked.length} game{unpicked.length === 1 ? '' : 's'} still to call.
              </strong>{' '}
              {unpickedWithLine.length === 0
                ? `No line has posted ${unpicked.length === 1 ? 'for it' : 'for them'} yet. They stay open until each game starts.`
                : unpickedWithLine.length === unpicked.length
                  ? 'Make the pick to claim the bonus.'
                  : `${unpickedWithLine.length} can be picked now; the rest are waiting on a line.`}
            </span>
            <button
              type="button"
              className="btn btn--sm btn--primary"
              onClick={() => {
                setTab('lineup');
                setEntryStep('picks');
                // Locked contests show the picker as its own card instead.
                window.setTimeout(
                  () => picksRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
                  60,
                );
              }}
            >
              Go to picks
            </button>
          </div>
        ) : null}

        {tab === 'lineup' && latePicksOpen ? (
          <div className="card" ref={picksRef}>
            <div className="section-title">
              <h2 style={{ fontSize: 15 }}>Still to call</h2>
              <span className="tiny faint">
                {stillOpen.length - unpicked.length}/{stillOpen.length} picked
              </span>
            </div>
            <p className="tiny muted" style={{ margin: '0 0 10px' }}>
              Your roster locked when the first game started, but a pick stays open until its own game does.
              These save as you make them.
            </p>
            <GamePicks
              games={stillOpen}
              picks={effectivePicks}
              contestId={contest.id}
              bonusPoints={contest.gameWinner.bonusPoints}
              onPick={(gameId, teamId) => {
                setLatePicks((current) => ({ ...current, [gameId]: teamId }));
                void setLatePick(contest.id, uid, gameId, teamId).catch(() =>
                  setToast({ tone: 'bad', text: 'Could not save that pick. Try again.' }),
                );
              }}
            />
          </div>
        ) : null}

        {tab === 'lineup' ? (
          locked ? (
            <LockedLineup
              contest={contest}
              playersById={playersById}
              lineup={myEntry?.lineup ?? []}
              picks={effectivePicks}
              hasEntry={Boolean(myEntry)}
              points={myRow?.total ?? 0}
              bonus={myRow?.bonusPoints ?? 0}
              pointDeltas={pointDeltas}
            />
          ) : (
            <div className="grid grid--builder">
              <div className="stack">
                {steps.length > 1 ? (
                  <div className="stepbar">
                    {steps.map((step, index) => {
                      const done = stepReady[step];
                      const current = step === entryStep;
                      // Going back is always allowed; going forward needs the
                      // steps before it finished.
                      const reachable =
                        index <= stepIndex || steps.slice(0, index).every((earlier) => stepReady[earlier]);
                      return (
                        <button
                          key={step}
                          type="button"
                          className={`stepbar__step${current ? ' stepbar__step--on' : ''}${done ? ' stepbar__step--done' : ''}`}
                          disabled={!reachable}
                          onClick={() => setEntryStep(step)}
                        >
                          <span className="stepbar__n">{done && !current ? '✓' : index + 1}</span>
                          {step === 'roster' ? 'Roster' : step === 'picks' ? 'Picks' : 'Money'}
                        </button>
                      );
                    })}
                  </div>
                ) : null}

                {entryStep === 'roster' ? (
                <>
                <HowItWorks
                  contest={contest}
                  hasCaptain={hasCaptain}
                  multiplier={capMultiplier}
                />

                {copyable.length > 0 ? (
                  <div className="card card--tight stack" style={{ gap: 8 }}>
                    <div>
                      <div className="eyebrow">Copy a lineup</div>
                      <div className="tiny faint">
                        Same games, same roster, same cap. Copying fills this lineup in — it stays a separate
                        entry, so changing one never changes the other.
                      </div>
                    </div>
                    <div className="row row--wrap" style={{ gap: 6 }}>
                      {copyable.map((source) => (
                        <button
                          key={source.contest.id}
                          type="button"
                          className="btn btn--sm"
                          onClick={() => copyLineupFrom(source)}
                        >
                          From {source.contest.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                {teamName ? (
                  <div className="card card--tight teamname">
                    <div>
                      <div className="eyebrow">Your team name</div>
                      <div className="teamname__value">{teamName}</div>
                    </div>
                    <button
                      type="button"
                      className="btn btn--sm btn--ghost"
                      onClick={() => identity && setTeamName(generateTeamName(identity.firstName))}
                    >
                      Shuffle
                    </button>
                  </div>
                ) : null}

                <div className="card card--tight">
                  <div className="row row--between" style={{ marginBottom: 8 }}>
                    <div className="eyebrow">Player pool · {visiblePlayers.length}</div>
                    {activeSlot ? (
                      <button type="button" className="btn btn--sm btn--ghost" onClick={() => setActiveSlotId(null)}>
                        {activeSlot.positions.includes('*')
                          ? `Filling ${activeSlot.label} ✕`
                          : `${activeSlot.label} only ✕`}
                      </button>
                    ) : null}
                  </div>

                  <input
                    className="input"
                    placeholder="Search player, team or position"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />

                  <div className="scroll-x" style={{ marginTop: 8 }}>
                    {contest.sports.length > 1 ? (
                      <>
                        <FilterChip active={sportFilter === 'all'} onClick={() => setSportFilter('all')}>
                          All sports
                        </FilterChip>
                        {contest.sports.map((sport) => (
                          <FilterChip
                            key={sport}
                            active={sportFilter === sport}
                            onClick={() => setSportFilter(sport)}
                          >
                            {SPORT_LABELS[sport]}
                          </FilterChip>
                        ))}
                      </>
                    ) : null}
                    <FilterChip active={sortKey === 'salary'} onClick={() => setSortKey('salary')}>
                      Salary
                    </FilterChip>
                    <FilterChip active={sortKey === 'projection'} onClick={() => setSortKey('projection')}>
                      Projection
                    </FilterChip>
                  </div>
                </div>

                <div className="list">
                  {visiblePlayers.slice(0, 220).map((player) => (
                    <PlayerCard
                      key={player.id}
                      player={player}
                      selected={usedPlayerIds.has(player.id)}
                      used={usedPlayerIds.has(player.id)}
                      unaffordable={player.salary > spendable}
                      captain={hasCaptain && player.id === captainPlayerId}
                      captainMultiplier={capMultiplier}
                      phase={phases.get(player.gameId)}
                      onClick={() => assignPlayer(player)}
                      onInfo={() => setDetail(player)}
                      onToggleCaptain={
                        hasCaptain && usedPlayerIds.has(player.id)
                          ? () => toggleCaptainForPlayer(player.id)
                          : undefined
                      }
                    />
                  ))}
                  {visiblePlayers.length === 0 ? (
                    <Empty title="No players match" hint="Clear the slot filter or search." />
                  ) : null}
                </div>
                </>
                ) : null}

                {entryStep === 'picks' ? (
                  <div className="card" ref={picksRef}>
                    <div className="section-title">
                      <h2 style={{ fontSize: 15 }}>Call the games</h2>
                      <span className="tiny faint">
                        {pickableGames.filter((game) => Boolean(picks[game.id])).length}/{pickableGames.length}
                      </span>
                    </div>
                    {/* A line that has not posted is not a reason to be stuck. */}
                    {awaitingLine.length > 0 ? (
                      <div className="banner banner--warn" style={{ marginBottom: 10 }}>
                        <strong>
                          {awaitingLine.length} {awaitingLine.length === 1 ? 'game has' : 'games have'} no line
                          yet.
                        </strong>{' '}
                        Submit without {awaitingLine.length === 1 ? 'it' : 'them'} — you will be asked to call{' '}
                        {awaitingLine.length === 1 ? 'it' : 'them'} here once the line posts, any time before
                        the first game starts.
                      </div>
                    ) : null}
                    <GamePicks
                      games={contest.games}
                      picks={picks}
                      contestId={contest.id}
                      bonusPoints={contest.gameWinner.bonusPoints}
                      onPick={(gameId, teamId) => setPicks((current) => ({ ...current, [gameId]: teamId }))}
                    />
                  </div>
                ) : null}

                {entryStep === 'wager' && contest.wager ? (
                  <WagerPrompt
                    contest={contest}
                    answer={wagerIn}
                    locked={locked}
                    bettors={bettors}
                    onAnswer={(value) => {
                      setWagerIn(value);
                      // Written straight away once they are an entrant; otherwise
                      // it rides along with the lineup they are about to submit.
                      if (myEntry) void saveWagerIn(contest.id, uid, value).catch(() => undefined);
                    }}
                  />
                ) : null}
              </div>

              <div className="stack" style={{ position: 'sticky', top: 70 }}>
                <div className="card">
                  <div className="section-title">
                    <h2 style={{ fontSize: 15 }}>Your lineup</h2>
                    <span className="tiny faint">
                      {validation?.filledSlots ?? 0} of {contest.rosterSlots.length} spots
                    </span>
                  </div>
                  <RosterPanel
                    phases={phases}
                    slots={contest.rosterSlots}
                    lineup={lineup}
                    playersById={playersById}
                    activeSlotId={activeSlotId}
                    onSelectSlot={(slotId) => setActiveSlotId(activeSlotId === slotId ? null : slotId)}
                    onRemove={removeSlot}
                    onToggleCaptain={hasCaptain ? toggleCaptainSlot : undefined}
                    captainMultiplier={hasCaptain ? capMultiplier : null}
                  />
                </div>

                {validation && validation.errors.length > 0 && entryStep === 'roster' ? (
                  <div className="card card--tight">
                    <div className="eyebrow" style={{ marginBottom: 6 }}>
                      Before you submit
                    </div>
                    <ul className="tiny muted" style={{ margin: 0, paddingLeft: 18 }}>
                      {validation.errors
                        .filter((error) => !isPickError(error))
                        .slice(0, 5)
                        .map((error) => (
                          <li key={error}>{error}</li>
                        ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            </div>
          )
        ) : null}

        {tab === 'board' ? (
          <>
            {status === 'complete' ? (
              <WagerSettlement
                contest={contest}
                rows={leaderboard}
                standings={standings}
                uid={uid}
                onTogglePaid={(payer, paid) =>
                  void setWagerPaid(contest.id, payer, paid).catch(() => undefined)
                }
              />
            ) : null}
            <LeaderboardList
              rows={leaderboard}
              contest={contest}
              locked={locked}
              pointDeltas={pointDeltas}
              onOpenPlayer={openPlayer}
            />
          </>
        ) : null}

        {tab === 'scoring' ? (
          <ScoringTab
            contest={contest}
            players={players}
            entries={locked ? entries : myEntry ? [myEntry] : []}
            pointDeltas={pointDeltas}
            locked={locked}
            selfUid={uid}
            onOpenPlayer={openPlayer}
          />
        ) : null}

        {tab === 'info' ? <ContestInfo contest={contest} /> : null}
      </div>

      {tab === 'lineup' && !locked ? (
        <SalaryBar
          cap={cap}
          used={salaryUsed}
          filled={validation?.filledSlots ?? 0}
          total={contest.rosterSlots.length}
          blocker={stepBlocker}
          action={
            stepIndex < steps.length - 1 ? (
              <button
                type="button"
                className="btn btn--primary"
                disabled={!stepReady[entryStep]}
                onClick={() => setEntryStep(steps[stepIndex + 1])}
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                className="btn btn--go"
                disabled={!canSubmit || saving || !identity}
                onClick={submit}
              >
                {saving ? <span className="spinner" /> : myEntry ? 'Update' : 'Submit'}
              </button>
            )
          }
        />
      ) : null}

      {detail ? <PlayerSheet player={detail} contest={contest} onClose={() => setDetail(null)} /> : null}
    </main>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button type="button" className={`tab${active ? ' tab--active' : ''}`} onClick={onClick} style={{ flex: 'none' }}>
      {children}
    </button>
  );
}

function LockedLineup({
  contest,
  playersById,
  lineup,
  picks,
  hasEntry,
  points,
  bonus,
  pointDeltas,
}: {
  contest: import('../types').Contest;
  playersById: Map<string, ContestPlayer>;
  lineup: LineupSelection[];
  picks: Record<string, string>;
  hasEntry: boolean;
  points: number;
  bonus: number;
  pointDeltas: Map<string, number>;
}) {
  const phases = phasesByGame(contest);

  if (!hasEntry) {
    return (
      <Empty
        title="You did not enter this contest"
        hint="Rosters locked when the first game started. You can still follow the leaderboard."
      />
    );
  }
  return (
    <div className="stack">
      <div className="card">
        <div className="row row--between">
          <div>
            <div className="eyebrow">Your total</div>
            <div style={{ fontSize: 30, fontWeight: 900 }} className="num">
              {points.toFixed(1)}
            </div>
          </div>
          {contest.gameWinner.enabled ? (
            <div style={{ textAlign: 'right' }}>
              <div className="eyebrow">Winner bonus</div>
              <div style={{ fontSize: 20, fontWeight: 800 }} className="num">
                +{bonus.toFixed(1)}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="card">
        <div className="section-title">
          <h2 style={{ fontSize: 15 }}>Locked roster</h2>
          <span className="tiny faint">live points</span>
        </div>
        <RosterPanel
          slots={contest.rosterSlots}
          lineup={lineup}
          playersById={playersById}
          captainMultiplier={captainEnabled(contest) ? captainMultiplier(contest) : null}
          pointDeltas={pointDeltas}
          phases={phases}
          live
          readOnly
        />
      </div>

      {contest.gameWinner.enabled ? (
        <div className="card">
          <div className="section-title">
            <h2 style={{ fontSize: 15 }}>Your spread picks</h2>
          </div>
          <GamePicks games={contest.games} picks={picks} bonusPoints={contest.gameWinner.bonusPoints} readOnly />
        </div>
      ) : null}
    </div>
  );
}

function ContestInfo({ contest }: { contest: import('../types').Contest }) {
  return (
    <div className="stack">
      <div className="card">
        <div className="section-title">
          <h2 style={{ fontSize: 15 }}>Games</h2>
          <span className="tiny faint">Locks at the first start</span>
        </div>
        <div className="list">
          {contest.games.map((game) => (
            <div className="card card--tight" key={game.id}>
              <div className="row row--between">
                <span style={{ fontWeight: 700 }}>{game.shortName}</span>
                <SportPill sport={game.sport} />
              </div>
              <div className="row row--between tiny muted" style={{ marginTop: 4 }}>
                <span>{formatGameTime(game.startTime)}</span>
                <span>
                  {game.state === 'pre'
                    ? 'Scheduled'
                    : `${game.away.abbreviation} ${game.away.score ?? 0} – ${game.home.score ?? 0} ${game.home.abbreviation} · ${game.statusDetail}`}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="section-title">
          <h2 style={{ fontSize: 15 }}>Automatic salary cap</h2>
        </div>
        <KeyValue label="Salary cap" value={formatMoney(contest.salaryCapInfo.cap)} />
        <KeyValue label="Cheapest valid lineup" value={formatMoney(contest.salaryCapInfo.minLineupCost)} />
        <KeyValue label="Median lineup" value={formatMoney(contest.salaryCapInfo.medianLineupCost)} />
        <KeyValue label="All-stars lineup" value={formatMoney(contest.salaryCapInfo.maxLineupCost)} />
        <p className="tiny faint" style={{ marginBottom: 0 }}>
          The cap is derived from this contest's own player pool, so it always leaves multiple viable builds
          while making a roster of every elite player impossible.
        </p>
      </div>

      {contest.sports.length > 1 || contest.sports[0] !== 'nfl' ? (
        <div className="card">
          <div className="section-title">
            <h2 style={{ fontSize: 15 }}>Cross-sport normalization</h2>
          </div>
          <p className="tiny muted" style={{ marginTop: 0 }}>
            NFL fantasy points are the baseline. Other sports are multiplied by a factor measured from this
            contest's expected scoring, so no sport dominates simply by accumulating more statistical events.
          </p>
          {contest.sports.map((sport) => (
            <KeyValue
              key={sport}
              label={`${SPORT_LABELS[sport]} factor`}
              value={
                <>
                  ×{(contest.normalization.factors[sport] ?? 1).toFixed(3)}
                  <span className="tiny faint">
                    {' '}
                    (anchor {(contest.normalization.anchors[sport] ?? 0).toFixed(1)})
                  </span>
                </>
              }
            />
          ))}
        </div>
      ) : null}

      {contest.gameWinner.enabled ? (
        <div className="card">
          <div className="section-title">
            <h2 style={{ fontSize: 15 }}>Spread pick bonus</h2>
          </div>
          <KeyValue label="Bonus per correct pick" value={`${contest.gameWinner.bonusPercent}%`} />
          <KeyValue label="Contest scoring baseline" value={contest.scoringBaseline.toFixed(1)} />
          <KeyValue label="Points per correct pick" value={`+${contest.gameWinner.bonusPoints.toFixed(1)}`} />
          <p className="tiny faint" style={{ marginBottom: 0 }}>
            The bonus is a percentage of a fixed contest baseline set when the contest was created — never of
            your own score — so every entrant earns exactly the same amount for a correct pick.
          </p>
        </div>
      ) : null}

      <div className="card">
        <div className="section-title">
          <h2 style={{ fontSize: 15 }}>Scoring</h2>
        </div>
        {contest.sports.map((sport) => {
          const table = contest.scoring[sport];
          if (!table) return null;
          return (
            <div key={sport} style={{ marginBottom: 12 }}>
              <div className="eyebrow" style={{ marginBottom: 6 }}>
                {SPORT_LABELS[sport]}
              </div>
              <table className="table-mini">
                <tbody>
                  {Object.entries(table.values).map(([key, value]) => (
                    <tr key={key}>
                      <td>{statMeta(sport, key).label}</td>
                      <td className="num">{value}</td>
                    </tr>
                  ))}
                  {Object.entries(table.tiers ?? {}).map(([key, tiers]) => (
                    <tr key={key}>
                      <td>{statMeta(sport, key).label}</td>
                      <td className="num tiny">
                        {tiers.map((tier) => `${tier.min}${tier.max === null ? '+' : `-${tier.max}`}: ${tier.points}`).join(' · ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** The contest's rules, stated before anyone starts picking. */
function HowItWorks({
  contest,
  hasCaptain,
  multiplier,
}: {
  contest: import('../types').Contest;
  hasCaptain: boolean;
  multiplier: number;
}) {
  return (
    <div className="card card--tight howto">
      <div className="eyebrow" style={{ marginBottom: 8 }}>
        How this contest works
      </div>
      <ul className="howto__list">
        <li>
          <strong>Tap a player</strong> to add them to your roster, and tap them again to drop them.
        </li>
        {hasCaptain ? (
          <li>
            Pick your captain on the roster itself: every spot you have filled has a{' '}
            <strong>make captain</strong> button. Your <strong>captain scores {multiplier}× points</strong>{' '}
            and costs <strong>{multiplier}× salary</strong>. The captain is{' '}
            <strong>one of your {contest.rosterSlots.length} spots</strong>, not an extra one — they just move
            to the top of the list.
          </li>
        ) : null}
        <li>
          Fill all <strong>{contest.rosterSlots.length} spots</strong> without going over the{' '}
          <strong>{formatMoney(contest.salaryCapInfo.cap)}</strong> salary cap.
        </li>
        {contest.gameWinner.enabled ? (
          contest.games.some((game) => Boolean(game.spread)) ? (
            <li>
              <strong>Pick one team in every game, against the spread.</strong> The favorite has points taken
              away and the underdog gets points added, to even the teams out. Each button tells you exactly
              what has to happen — "must win by 4+", "can lose by up to 3, or win". Getting it right adds{' '}
              <strong>+{contest.gameWinner.bonusPoints.toFixed(1)}</strong>, the same for everyone.
            </li>
          ) : (
            <li>
              Pick a winner in every game. Each correct pick adds{' '}
              <strong>+{contest.gameWinner.bonusPoints.toFixed(1)}</strong> — the same for everyone.
            </li>
          )
        ) : null}
        <li>
          Everything locks when the first game starts. Until then, nobody can see your roster.
        </li>
      </ul>
    </div>
  );
}
